import React, { useEffect } from 'react';
import ProjectDialog from './components/ProjectDialog';
import QuoteBar from './components/QuoteBar';
import Sidebar from './components/Sidebar';
import Testimonials from './components/Testimonials';
import Topbar from './components/Topbar';
import AboutSection from './components/sections/AboutSection';
import BlogSection from './components/sections/BlogSection';
import ContactSection from './components/sections/ContactSection';
import ProjectsSection from './components/sections/ProjectsSection';
import StackSection from './components/sections/StackSection';
import { content } from './content';
import { useHashRoute } from './hooks/useHashRoute';
import { usePersistentState } from './hooks/usePersistentState';
import { Theme, ViewMode } from './types';
import './styles/App.css';

const isTheme = (value: unknown): value is Theme =>
  value === 'dark' || value === 'light';

const isViewMode = (value: unknown): value is ViewMode =>
  value === 'grid' || value === 'list';

/**
 * No stored choice yet: the site's default is light, regardless of the
 * visitor's OS setting. Dark stays one click away on the toggle.
 */
const defaultTheme = (): Theme => 'light';

/** Keeps the browser UI (address bar, tab strip) on the same theme as the page. */
const syncThemeColor = (theme: Theme) => {
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', theme === 'dark' ? '#010408' : '#f4f5f7');
};

const App = () => {
  const { route, navigate, openProject, closeProject, openPost, closePost } =
    useHashRoute();
  const { section, postId } = route;
  const [view, setView] = usePersistentState<ViewMode>(
    'ideaprojects.view',
    () => 'grid',
    isViewMode
  );
  const [theme, setTheme] = usePersistentState<Theme>(
    'ideaprojects.theme',
    defaultTheme,
    isTheme
  );

  const activeProject =
    content.projects.find((project) => project.id === route.projectId) ?? null;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    syncThemeColor(theme);
  }, [theme]);

  const openPostTitle =
    section === 'blog'
      ? content.blog.posts.find((post) => post.id === postId)?.title
      : undefined;

  // An open article names itself in the tab, and opening one scrolls back to
  // the top the same way switching sections does — it is a new page to read,
  // not a longer version of the one already scrolled.
  useEffect(() => {
    document.title = content.ui.documentTitle(
      openPostTitle ?? content.headings[section].title
    );
    window.scrollTo({ top: 0 });
  }, [section, postId, openPostTitle]);

  return (
    <div className="layout">
      <Sidebar active={section} onNavigate={navigate} />

      <main className="content">
        <Topbar
          section={section}
          postOpen={Boolean(postId)}
          view={view}
          onViewChange={setView}
          theme={theme}
          onThemeToggle={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        />

        <div key={`${section}${postId ?? ''}`} className="content__body">
          {section === 'projects' && (
            <ProjectsSection
              view={view}
              onOpen={(project) => openProject(project.id)}
            />
          )}
          {section === 'stack' && <StackSection />}
          {section === 'blog' && (
            <BlogSection postId={postId} onOpen={openPost} onBack={closePost} />
          )}
          {section === 'about' && <AboutSection />}
          {section === 'testimonials' && <Testimonials />}
          {section === 'contact' && <ContactSection />}

          {/* Every section ends with somewhere to go. Contacts is skipped —
              the whole section is already the ask — and so is an open
              article, which closes with an ask of its own. */}
          {section !== 'contact' && !postId && <QuoteBar />}
        </div>
      </main>

      {activeProject?.details && (
        <ProjectDialog project={activeProject} onClose={closeProject} />
      )}
    </div>
  );
};

export default App;
